import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Undo,
  Redo,
  Type,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Minus,
  Table as TableIcon,
  Columns,
  Rows,
  Code,
  Check,
  Copy,
  ChevronDown,
  ChevronRight,
  X,
  Maximize2,
  Minimize2,
  Search,
  Database,
  FileText,
} from "lucide-react";
import {
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdGridOn,
} from "react-icons/md";

// --- Helper: Debounce ---
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// --- Helper: Normalize HTML ---
const normalizeHtmlForExport = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Remove empty paragraphs
  doc.querySelectorAll("p").forEach((p) => {
    if (!p.textContent?.trim() && !p.children.length) {
      p.remove();
    }
  });

  // Add inline styles to tables for proper rendering in PDFs
  doc.querySelectorAll("table").forEach((table) => {
    const existingStyle = table.getAttribute("style") || "";
    table.setAttribute(
      "style",
      `${existingStyle}; border-collapse: collapse; width: 100%; margin: 1em 0;`.trim()
    );
  });

  // Add inline styles to table cells (th, td)
  doc.querySelectorAll("th, td").forEach((cell) => {
    const existingStyle = cell.getAttribute("style") || "";
    const isHeader = cell.tagName.toLowerCase() === "th";
    const cellStyle = isHeader
      ? "border: 1px solid #e5e7eb; padding: 8px 12px; background-color: #f9fafb; font-weight: 600; text-align: left;"
      : "border: 1px solid #e5e7eb; padding: 8px 12px;";

    cell.setAttribute(
      "style",
      `${existingStyle}; ${cellStyle}`.trim()
    );
  });

  // Add styles to headings
  doc.querySelectorAll("h1").forEach((h) => {
    const existingStyle = h.getAttribute("style") || "";
    h.setAttribute("style", `${existingStyle}; font-size: 2em; font-weight: 700; margin-bottom: 0.5em;`.trim());
  });

  doc.querySelectorAll("h2").forEach((h) => {
    const existingStyle = h.getAttribute("style") || "";
    h.setAttribute("style", `${existingStyle}; font-size: 1.5em; font-weight: 600; margin-bottom: 0.5em;`.trim());
  });

  doc.querySelectorAll("h3").forEach((h) => {
    const existingStyle = h.getAttribute("style") || "";
    h.setAttribute("style", `${existingStyle}; font-size: 1.25em; font-weight: 600; margin-bottom: 0.5em;`.trim());
  });

  // Add styles to paragraphs
  doc.querySelectorAll("p").forEach((p) => {
    const existingStyle = p.getAttribute("style") || "";
    p.setAttribute("style", `${existingStyle}; margin-bottom: 0.75em; line-height: 1.6;`.trim());
  });

  // Add styles to lists
  doc.querySelectorAll("ul").forEach((ul) => {
    const existingStyle = ul.getAttribute("style") || "";
    ul.setAttribute("style", `${existingStyle}; padding-left: 1.5em; margin-bottom: 1em; list-style-type: disc;`.trim());
  });

  doc.querySelectorAll("ol").forEach((ol) => {
    const existingStyle = ol.getAttribute("style") || "";
    ol.setAttribute("style", `${existingStyle}; padding-left: 1.5em; margin-bottom: 1em; list-style-type: decimal;`.trim());
  });

  // Add styles to blockquotes
  doc.querySelectorAll("blockquote").forEach((bq) => {
    const existingStyle = bq.getAttribute("style") || "";
    bq.setAttribute("style", `${existingStyle}; border-left: 4px solid #e5e7eb; padding-left: 1em; margin: 1em 0; color: #6b7280;`.trim());
  });

  // Add styles to hr
  doc.querySelectorAll("hr").forEach((hr) => {
    const existingStyle = hr.getAttribute("style") || "";
    hr.setAttribute("style", `${existingStyle}; border: none; border-top: 1px solid #e5e7eb; margin: 2em 0;`.trim());
  });

  return doc.body.innerHTML;
};

// --- UI Components ---
const Btn = ({ onClick, active, title, disabled, children }: any) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded-md transition-all flex items-center justify-center ${
      active
        ? "bg-red-50 text-red-600 shadow-sm ring-1 ring-red-200"
        : disabled
        ? "text-gray-300 cursor-not-allowed"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`}
  >
    {children}
  </button>
);

const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  variables?: Record<string, any>;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = "Start typing your PDF template…",
  variables = {},
}) => {
  const [htmlValue, setHtmlValue] = useState(content);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isCodeView, setIsCodeView] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTreeNodes, setExpandedTreeNodes] = useState<Set<string>>(new Set());
  const [expandedTreePaths, setExpandedTreePaths] = useState<Set<string>>(new Set());
  
  // Menu states
  const [showAlignMenu, setShowAlignMenu] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);

  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const headingBtnRef = useRef<HTMLButtonElement>(null);
  const alignBtnRef = useRef<HTMLButtonElement>(null);
  const tableBtnRef = useRef<HTMLButtonElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "tiptap-empty",
      }),
    ],
    content: content || "<p></p>",
    editorProps: {
      handleDOMEvents: {
        drop: (view, event) => {
          event.preventDefault();
          event.stopPropagation();

          const variable = (event as DragEvent).dataTransfer?.getData(
            "text/plain"
          );
          if (!variable) return true;

          try {
            const pos = view.posAtCoords({
              left: (event as DragEvent).clientX,
              top: (event as DragEvent).clientY,
            });

            let textToInsert = variable.trim();
            if (!textToInsert.startsWith("{{") || !textToInsert.endsWith("}}")) {
              textToInsert = `{{${textToInsert}}}`;
            }

            if (pos && typeof pos.pos === "number") {
              view.dispatch(
                view.state.tr.insertText(textToInsert, pos.pos)
              );
            } else {
              view.dispatch(view.state.tr.insertText(textToInsert));
            }
          } catch (e) {
            let textToInsert = variable.trim();
            if (!textToInsert.startsWith("{{") || !textToInsert.endsWith("}}")) {
              textToInsert = `{{${textToInsert}}}`;
            }
            view.dispatch(view.state.tr.insertText(textToInsert));
          }

          return true;
        },
      },
    },
  });

  // Sync content when prop changes (if different)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      if (!editor.isFocused || content === "<p></p>" || content === "") {
        editor.commands.setContent(content, { emitUpdate: false });
      }
    }
  }, [content, editor]);


  // Update htmlValue and parent when editor content changes (debounced)
  useEffect(() => {
    if (!editor) return;
    const update = debounce(() => {
      const rawHtml = editor.getHTML();
      const normalized = normalizeHtmlForExport(rawHtml);
      setHtmlValue(normalized);
      onChange(normalized);
    }, 150);
    
    editor.on("transaction", update);
    return () => {
      editor.off("transaction", update);
    };
  }, [editor, onChange]);

  // Apply HTML code into the editor (safe normalized HTML)
  const applyHtmlToEditor = useCallback(
    debounce((htmlStr: string) => {
      if (!editor) return;
      const normalized = normalizeHtmlForExport(htmlStr);
      try {
        editor.commands.setContent(normalized);
      } catch (err) {
        editor.commands.setContent(normalized);
      }
      setHtmlValue(normalized);
      onChange(normalized);
    }, 200),
    [editor, onChange]
  );

  // Copy HTML to clipboard (normalized)
  const copyHtmlToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(htmlValue);
    } catch (err) {
      // ignore
    }
  };

  // When user presses apply from textarea
  const onApplyHtmlClick = () => {
    applyHtmlToEditor(htmlValue);
    setIsCodeView(false);
  };

  const isEditorEmpty =
    !editor || editor.isEmpty || editor.getText().trim().length === 0;

  // Recursively flatten variables for nested key access
  const flattenedVariables = useMemo(() => {
    const flat: Record<string, any> = {};
    const flatten = (baseKey: string, value: any) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.entries(value).forEach(([subKey, subVal]) => {
          flatten(`${baseKey}.${subKey}`, subVal);
        });
      } else {
        flat[baseKey] = value;
      }
    };
    Object.entries(variables || {}).forEach(([key, value]) => {
      flatten(key, value);
    });
    return flat;
  }, [variables]);

  // Replace variables in HTML for preview
  const getPreviewHtml = useCallback(() => {
    let previewHtml = htmlValue;
    Object.entries(flattenedVariables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      previewHtml = previewHtml.replace(regex, displayValue);
    });
    return previewHtml;
  }, [htmlValue, flattenedVariables]);

  const groupedTreeVariables = useMemo(() => {
    const tree: Record<string, { inputs: Record<string, any>; outputs: Record<string, any> }> = {};

    Object.entries(variables || {}).forEach(([key, value]) => {
      const parts = key.split(".");
      const nodeName = parts[0];
      const type = parts[1] as "inputs" | "outputs";
      const fieldName = parts.slice(2).join(".");

      if (!nodeName || !type || !fieldName) return;

      if (!tree[nodeName]) {
        tree[nodeName] = { inputs: {}, outputs: {} };
      }
      tree[nodeName][type][fieldName] = value;
    });

    return tree;
  }, [variables]);

  // Expand all tree nodes by default on load
  useEffect(() => {
    if (Object.keys(groupedTreeVariables).length > 0) {
      setExpandedTreeNodes((prev) => {
        const next = new Set(prev);
        Object.keys(groupedTreeVariables).forEach((nodeName) => {
          next.add(nodeName);
        });
        return next;
      });
    }
  }, [groupedTreeVariables]);

  const toggleTreeNodeExpansion = (nodeName: string) => {
    setExpandedTreeNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeName)) next.delete(nodeName);
      else next.add(nodeName);
      return next;
    });
  };

  const toggleTreePathExpansion = (path: string) => {
    setExpandedTreePaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const renderNestedValue = (
    keyName: string,
    value: any,
    fullPath: string,
    level: number = 0
  ): JSX.Element => {
    const pathId = fullPath;
    const isExpanded = expandedTreePaths.has(pathId);
    const query = searchQuery.toLowerCase().trim();

    if (Array.isArray(value)) {
      const hasItems = value.length > 0;
      if (query && !keyName.toLowerCase().includes(query) && !JSON.stringify(value).toLowerCase().includes(query)) {
        return <></>;
      }

      return (
        <div key={fullPath} className="space-y-0.5" style={{ paddingLeft: `${level ? 12 : 0}px` }}>
          <div
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.effectAllowed = "copy";
              e.dataTransfer.setData("text/plain", `{{${fullPath}}}`);
            }}
            onClick={() => {
              if (editor) {
                editor.chain().focus().insertContent(`{{${fullPath}}}`).run();
              }
            }}
            className="group flex items-center justify-between py-1 px-2 rounded cursor-pointer hover:bg-indigo-50/50 border border-transparent hover:border-indigo-150 transition-colors"
            title="Click to insert or drag"
          >
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className="cursor-pointer shrink-0 p-0.5 hover:bg-gray-200 rounded" onClick={(e) => {
                e.stopPropagation();
                if (hasItems) toggleTreePathExpansion(pathId);
              }}>
                {hasItems ? (
                  isExpanded ? (
                    <ChevronDown className="w-3 h-3 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-gray-500" />
                  )
                ) : (
                  <div className="w-3 h-3" />
                )}
              </span>
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full shrink-0"></div>
              <span className="text-xs font-mono font-medium text-gray-700 truncate">
                {keyName}
              </span>
              <span className="text-[9px] font-semibold text-purple-600 bg-purple-50 px-1 rounded shrink-0">
                [{value.length}]
              </span>
            </div>
            <span className="opacity-0 group-hover:opacity-100 text-[9px] font-bold text-indigo-600 ml-1 shrink-0">
              Insert +
            </span>
          </div>

          {isExpanded && hasItems && (
            <div className="space-y-0.5">
              {value.map((item, index) => {
                const arrayKey = `[${index}]`;
                const arrayPath = `${fullPath}[${index}]`;
                return renderNestedValue(arrayKey, item, arrayPath, level + 1);
              })}
            </div>
          )}
        </div>
      );
    }

    if (value && typeof value === "object") {
      const hasChildren = Object.keys(value).length > 0;
      if (query && !keyName.toLowerCase().includes(query) && !JSON.stringify(value).toLowerCase().includes(query)) {
        return <></>;
      }

      return (
        <div key={fullPath} className="space-y-0.5" style={{ paddingLeft: `${level ? 12 : 0}px` }}>
          <div
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.effectAllowed = "copy";
              e.dataTransfer.setData("text/plain", `{{${fullPath}}}`);
            }}
            onClick={() => {
              if (editor) {
                editor.chain().focus().insertContent(`{{${fullPath}}}`).run();
              }
            }}
            className="group flex items-center justify-between py-1 px-2 rounded cursor-pointer hover:bg-indigo-50/50 border border-transparent hover:border-indigo-150 transition-colors"
            title="Click to insert or drag"
          >
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className="cursor-pointer shrink-0 p-0.5 hover:bg-gray-200 rounded" onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) toggleTreePathExpansion(pathId);
              }}>
                {hasChildren ? (
                  isExpanded ? (
                    <ChevronDown className="w-3 h-3 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-gray-500" />
                  )
                ) : (
                  <div className="w-3 h-3" />
                )}
              </span>
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0"></div>
              <span className="text-xs font-mono font-medium text-gray-700 truncate">
                {keyName}
              </span>
            </div>
            <span className="opacity-0 group-hover:opacity-100 text-[9px] font-bold text-indigo-600 ml-1 shrink-0">
              Insert +
            </span>
          </div>

          {isExpanded && hasChildren && (
            <div className="space-y-0.5">
              {Object.entries(value).map(([subKey, subVal]) => {
                const subPath = `${fullPath}.${subKey}`;
                return renderNestedValue(subKey, subVal, subPath, level + 1);
              })}
            </div>
          )}
        </div>
      );
    }

    if (query && !keyName.toLowerCase().includes(query) && !String(value).toLowerCase().includes(query)) {
      return <></>;
    }

    return (
      <div key={fullPath} className="space-y-0.5" style={{ paddingLeft: `${level ? 12 : 0}px` }}>
        <div
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = "copy";
            e.dataTransfer.setData("text/plain", `{{${fullPath}}}`);
          }}
          onClick={() => {
            if (editor) {
              editor.chain().focus().insertContent(`{{${fullPath}}}`).run();
            }
          }}
          className="group flex items-center justify-between py-1 px-2 rounded-md bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm hover:bg-indigo-50/30 cursor-pointer transition-all duration-200"
          title="Click to insert or drag"
        >
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full shrink-0"></div>
            <span className="text-xs font-mono font-medium text-gray-700 truncate">
              {keyName}
            </span>
            <span className="text-[10px] text-gray-400 truncate max-w-[120px] font-mono italic">
              {value === null ? "null" : typeof value === "boolean" ? (value ? "true" : "false") : String(value)}
            </span>
          </div>
          <span className="opacity-0 group-hover:opacity-100 text-[9px] font-bold text-indigo-600 ml-1 shrink-0">
            Insert +
          </span>
        </div>
      </div>
    );
  };

  const renderVariablesSidebar = () => {
    return (
      <aside className="w-80 shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col h-full overflow-hidden select-none">
        <div className="p-4 border-b border-gray-200 bg-white shrink-0">
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-indigo-600 animate-pulse" />
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Available Variables</span>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search variables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
          {Object.keys(groupedTreeVariables).length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-xs">
              No variables found
            </div>
          ) : (
            Object.entries(groupedTreeVariables).map(([nodeName, sections]) => {
              const isExpanded = expandedTreeNodes.has(nodeName);
              const hasOutputs = Object.keys(sections.outputs).length > 0;
              const hasInputs = Object.keys(sections.inputs).length > 0;

              return (
                <div
                  key={nodeName}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                >
                  <div
                    className="px-3 py-2 cursor-pointer flex items-center justify-between bg-indigo-50/50 hover:bg-indigo-50 transition-colors"
                    onClick={() => toggleTreeNodeExpansion(nodeName)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                      {isExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5 text-indigo-700" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-indigo-700" />
                      )}
                      <span className="text-xs font-semibold text-indigo-700 truncate">
                        {nodeName}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-3 space-y-3 max-h-[50vh] overflow-y-auto">
                      {/* Outputs Section */}
                      {hasOutputs && (
                        <div className="space-y-1">
                          <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-wide px-2 border-b border-gray-100 pb-1 mb-1.5">
                            Outputs
                          </h5>
                          <div className="space-y-1">
                            {Object.entries(sections.outputs).map(([fieldName, val]) => {
                              return renderNestedValue(
                                fieldName,
                                val,
                                `${nodeName}.outputs.${fieldName}`,
                                0
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Inputs Section */}
                      {hasInputs && (
                        <div className="space-y-1">
                          <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-wide px-2 border-b border-gray-100 pb-1 mb-1.5">
                            Inputs
                          </h5>
                          <div className="space-y-1">
                            {Object.entries(sections.inputs).map(([fieldName, val]) => {
                              return renderNestedValue(
                                fieldName,
                                val,
                                `${nodeName}.inputs.${fieldName}`,
                                0
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="p-3.5 bg-indigo-50 border-t border-indigo-100 shrink-0 text-xs text-indigo-700 leading-relaxed">
          💡 <strong>Tip:</strong> Click any variable to insert it directly at your current cursor position, or drag and drop it.
        </div>
      </aside>
    );
  };

  const editorMarkup = (
    <div className={`bg-white border-gray-200 flex flex-col flex-1 min-h-0 ${
      isFullscreen ? "w-full h-full border-none rounded-none" : "rounded-xl border shadow-sm relative h-full"
    }`}>
      {/* Header / Toolbar */}
      <div className="border-b border-gray-200 bg-white px-4 py-2 flex items-center justify-start gap-4 shrink-0 z-20 overflow-x-auto no-scrollbar">
        {/* Left: Formatting Tools */}
        <div className="flex items-center gap-1">
          {/* History Group */}
          <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200 mr-2">
            <Btn
              onClick={() => editor?.chain().focus().undo().run()}
              disabled={!editor?.can().undo()}
              title="Undo (Ctrl+Z)"
            >
              <Undo className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() => editor?.chain().focus().redo().run()}
              disabled={!editor?.can().redo()}
              title="Redo (Ctrl+Y)"
            >
              <Redo className="w-4 h-4" />
            </Btn>
          </div>

          <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200 mr-2">
            <div className="relative">
              <button
                ref={headingBtnRef}
                onClick={() => setShowHeadingMenu((p) => !p)}
                className={`h-8 px-2 rounded flex items-center gap-1 transition-all ${
                  showHeadingMenu
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
                title="Text Style"
              >
                {editor?.isActive("heading", { level: 1 }) ? (
                  <Heading1 className="w-4 h-4" />
                ) : editor?.isActive("heading", {
                    level: 2,
                  }) ? (
                  <Heading2 className="w-4 h-4" />
                ) : editor?.isActive("heading", {
                    level: 3,
                  }) ? (
                  <Heading3 className="w-4 h-4" />
                ) : (
                  <Type className="w-4 h-4" />
                )}
                <ChevronDown className="w-3 h-3 opacity-50" />
              </button>
              {showHeadingMenu && (
                <Portal>
                  <div
                    className="fixed z-[999999] bg-white border border-gray-200 rounded-lg shadow-xl p-1 flex flex-col gap-0.5 min-w-[140px]"
                    style={{
                      top:
                        (headingBtnRef.current?.getBoundingClientRect()
                          .bottom || 0) + 4,
                      left:
                        headingBtnRef.current?.getBoundingClientRect()
                          .left || 0,
                    }}
                  >
                    {[
                      {
                        icon: Type,
                        label: "Paragraph",
                        active: editor?.isActive("paragraph"),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .setParagraph()
                            .run(),
                      },
                      {
                        icon: Heading1,
                        label: "Heading 1",
                        active: editor?.isActive("heading", {
                          level: 1,
                        }),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .toggleHeading({ level: 1 })
                            .run(),
                      },
                      {
                        icon: Heading2,
                        label: "Heading 2",
                        active: editor?.isActive("heading", {
                          level: 2,
                        }),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .toggleHeading({ level: 2 })
                            .run(),
                      },
                      {
                        icon: Heading3,
                        label: "Heading 3",
                        active: editor?.isActive("heading", {
                          level: 3,
                        }),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .toggleHeading({ level: 3 })
                            .run(),
                      },
                    ].map((item) => (
                      <button
                        key={item.label}
                        onClick={() => {
                          item.action();
                          setShowHeadingMenu(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-2 text-xs rounded-md transition-colors w-full text-left ${
                          item.active
                            ? "bg-red-50 text-red-600"
                            : "text-gray-700 hover:bg-gray-50 hover:text-red-600"
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    ))}
                  </div>
                </Portal>
              )}
            </div>
          </div>

          {/* Formatting Group */}
          <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200 mr-2">
            <Btn
              onClick={() =>
                editor?.chain().focus().toggleBold().run()
              }
              active={editor?.isActive("bold")}
              title="Bold (Ctrl+B)"
            >
              <Bold className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() =>
                editor?.chain().focus().toggleItalic().run()
              }
              active={editor?.isActive("italic")}
              title="Italic (Ctrl+I)"
            >
              <Italic className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() =>
                editor?.chain().focus().toggleUnderline().run()
              }
              active={editor?.isActive("underline")}
              title="Underline (Ctrl+U)"
            >
              <UnderlineIcon className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() =>
                editor?.chain().focus().toggleStrike().run()
              }
              active={editor?.isActive("strike")}
              title="Strikethrough"
            >
              <Strikethrough className="w-4 h-4" />
            </Btn>
          </div>

          {/* Lists & Indent Group */}
          <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200 mr-2">
            <Btn
              onClick={() =>
                editor?.chain().focus().toggleBulletList().run()
              }
              active={editor?.isActive("bulletList")}
              title="Bullet List"
            >
              <MdFormatListBulleted className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() =>
                editor?.chain().focus().toggleOrderedList().run()
              }
              active={editor?.isActive("orderedList")}
              title="Numbered List"
            >
              <MdFormatListNumbered className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() =>
                editor?.chain().focus().toggleBlockquote().run()
              }
              active={editor?.isActive("blockquote")}
              title="Blockquote"
            >
              <Quote className="w-4 h-4" />
            </Btn>
            <Btn
              onClick={() =>
                editor?.chain().focus().setHorizontalRule().run()
              }
              title="Horizontal Rule"
            >
              <Minus className="w-4 h-4" />
            </Btn>
          </div>

          {/* Alignment Group */}
          <div className="flex items-center gap-0.5 pr-2 border-r border-gray-200 mr-2">
            <div className="relative">
              <button
                ref={alignBtnRef}
                onClick={() => setShowAlignMenu((p) => !p)}
                className={`h-8 px-2 rounded flex items-center gap-1 transition-all ${
                  showAlignMenu
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
                title="Alignment"
              >
                {editor?.isActive({ textAlign: "center" }) ? (
                  <AlignCenter className="w-4 h-4" />
                ) : editor?.isActive({ textAlign: "right" }) ? (
                  <AlignRight className="w-4 h-4" />
                ) : editor?.isActive({ textAlign: "justify" }) ? (
                  <AlignJustify className="w-4 h-4" />
                ) : (
                  <AlignLeft className="w-4 h-4" />
                )}
                <ChevronDown className="w-3 h-3 opacity-50" />
              </button>
              {showAlignMenu && (
                <Portal>
                  <div
                    className="fixed z-[999999] bg-white border border-gray-200 rounded-lg shadow-xl p-1 flex flex-col gap-0.5 min-w-[120px]"
                    style={{
                      top:
                        (alignBtnRef.current?.getBoundingClientRect()
                          .bottom || 0) + 4,
                      left:
                        alignBtnRef.current?.getBoundingClientRect()
                          .left || 0,
                    }}
                  >
                    {[
                      {
                        icon: AlignLeft,
                        label: "Left",
                        active: editor?.isActive({ textAlign: "left" }),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .setTextAlign("left")
                            .run(),
                      },
                      {
                        icon: AlignCenter,
                        label: "Center",
                        active: editor?.isActive({ textAlign: "center" }),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .setTextAlign("center")
                            .run(),
                      },
                      {
                        icon: AlignRight,
                        label: "Right",
                        active: editor?.isActive({ textAlign: "right" }),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .setTextAlign("right")
                            .run(),
                      },
                      {
                        icon: AlignJustify,
                        label: "Justify",
                        active: editor?.isActive({ textAlign: "justify" }),
                        action: () =>
                          editor
                            ?.chain()
                            .focus()
                            .setTextAlign("justify")
                            .run(),
                      },
                    ].map((item) => (
                      <button
                        key={item.label}
                        onClick={() => {
                          item.action();
                          setShowAlignMenu(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-2 text-xs rounded-md transition-colors w-full text-left ${
                          item.active
                            ? "bg-red-50 text-red-600"
                            : "text-gray-700 hover:bg-gray-50 hover:text-red-600"
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    ))}
                  </div>
                </Portal>
              )}
            </div>
          </div>

          {/* Table Group */}
          <div className="flex items-center gap-0.5">
            <div className="relative">
              <button
                ref={tableBtnRef}
                onClick={() => setShowTableMenu((p) => !p)}
                className={`h-8 px-2 rounded flex items-center gap-1 transition-all ${
                  showTableMenu
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
                title="Table"
              >
                <MdGridOn className="w-4 h-4" />
                <ChevronDown className="w-3 h-3 opacity-50" />
              </button>
              {showTableMenu && (
                <Portal>
                  <div
                    className="fixed z-[999999] bg-white border border-gray-200 rounded-lg shadow-xl p-1 flex flex-col gap-0.5 min-w-[160px]"
                    style={{
                      top:
                        (tableBtnRef.current?.getBoundingClientRect()
                          .bottom || 0) + 4,
                      left:
                        tableBtnRef.current?.getBoundingClientRect()
                          .left || 0,
                    }}
                  >
                    <button
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-md w-full text-left"
                      onClick={() => {
                        editor
                          ?.chain()
                          .focus()
                          .insertTable({
                            rows: 3,
                            cols: 3,
                            withHeaderRow: true,
                          })
                          .run();
                        setShowTableMenu(false);
                      }}
                    >
                      <TableIcon className="w-4 h-4" /> Insert Table
                    </button>
                    <div className="h-px bg-gray-100 my-0.5" />
                    <button
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-md w-full text-left"
                      onClick={() => {
                        editor?.chain().focus().addColumnBefore().run();
                      }}
                    >
                      <Columns className="w-4 h-4" /> Add Col Before
                    </button>
                    <button
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-md w-full text-left"
                      onClick={() => {
                        editor?.chain().focus().addColumnAfter().run();
                      }}
                    >
                      <Columns className="w-4 h-4" /> Add Col After
                    </button>
                    <button
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-md w-full text-left"
                      onClick={() => {
                        editor?.chain().focus().deleteColumn().run();
                      }}
                    >
                      <Columns className="w-4 h-4" /> Delete Col
                    </button>
                    <div className="h-px bg-gray-100 my-0.5" />
                    <button
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-md w-full text-left"
                      onClick={() => {
                        editor?.chain().focus().addRowBefore().run();
                      }}
                    >
                      <Rows className="w-4 h-4" /> Add Row Before
                    </button>
                    <button
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-md w-full text-left"
                      onClick={() => {
                        editor?.chain().focus().addRowAfter().run();
                      }}
                    >
                      <Rows className="w-4 h-4" /> Add Row After
                    </button>
                    <button
                      className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 hover:text-red-600 rounded-md w-full text-left"
                      onClick={() => {
                        editor?.chain().focus().deleteRow().run();
                      }}
                    >
                      <Rows className="w-4 h-4" /> Delete Row
                    </button>
                  </div>
                </Portal>
              )}
            </div>
          </div>
        </div>

        {/* Right: View Toggles & Maximize button */}
        <div className="flex items-center gap-2 ml-auto sm:ml-0 shrink-0">
          <div className="flex items-center bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => {
                setIsCodeView(false);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                !isCodeView
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Editor
            </button>
            <button
              onClick={() => {
                setIsCodeView(false);
                setShowPreviewModal(true);
              }}
              disabled={isEditorEmpty}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                showPreviewModal
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              } disabled:opacity-50`}
            >
              Preview
            </button>
            <button
              onClick={() => {
                setIsCodeView(true);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                isCodeView
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Code className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsFullscreen((prev) => !prev)}
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors border border-gray-200 shadow-sm flex items-center justify-center bg-white"
            title={isFullscreen ? "Exit Fullscreen" : "Expand to Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative bg-white min-h-0">
        {isCodeView ? (
          <div className="absolute inset-0 flex flex-col bg-[#1e1e1e]">
            <div className="flex items-center justify-between px-4 py-2 bg-[#252526] border-b border-[#3e3e42]">
              <span className="text-xs text-gray-400 font-mono">
                HTML Source
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    await copyHtmlToClipboard();
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                  className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                >
                  {copied ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            <textarea
              ref={htmlRef}
              defaultValue={htmlValue}
              onChange={(e) => setHtmlValue(e.target.value)}
              className="flex-1 w-full resize-none outline-none text-sm font-mono px-4 py-4 bg-[#1e1e1e] text-[#d4d4d4] leading-relaxed"
              spellCheck={false}
            />
            <div className="p-3 bg-[#252526] border-t border-[#3e3e42] flex justify-end gap-2">
              <button
                onClick={onApplyHtmlClick}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                Apply Changes
              </button>
            </div>
          </div>
        ) : (
          <div
            className="absolute inset-0 overflow-auto"
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const variable =
                e.dataTransfer?.getData("text/plain") || "";
              if (variable && editor) {
                let textToInsert = variable.trim();
                if (!textToInsert.startsWith("{{") || !textToInsert.endsWith("}}")) {
                  textToInsert = `{{${textToInsert}}}`;
                }
                try {
                  editor
                    .chain()
                    .focus()
                    .insertContent(textToInsert)
                    .run();
                } catch {
                  editor.commands.insertContent(
                    textToInsert
                  );
                }
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <style>{`
              .tiptap { 
                min-height: 100%; 
                outline: none; 
                padding: 2rem;
              }
              .tiptap-empty::before {
                content: attr(data-placeholder);
                color: #9ca3af;
                pointer-events: none;
                position: absolute;
                top: 2rem;
                left: 2rem;
                font-size: 14px;
              }
              .tiptap p { margin-bottom: 0.75em; line-height: 1.6; }
              .tiptap h1 { font-size: 2em; font-weight: 700; margin-bottom: 0.5em; }
              .tiptap h2 { font-size: 1.5em; font-weight: 600; margin-bottom: 0.5em; }
              .tiptap ul, .tiptap ol { padding-left: 1.5em; margin-bottom: 1em; }
              .tiptap ul { list-style-type: disc; }
              .tiptap ol { list-style-type: decimal; }
              .tiptap table { border-collapse: collapse; width: 100%; margin: 1em 0; }
              .tiptap th, .tiptap td { border: 1px solid #e5e7eb; padding: 8px 12px; position: relative; }
              .tiptap th { background-color: #f9fafb; font-weight: 600; text-align: left; }
              .tiptap .selectedCell:after {
                z-index: 2;
                position: absolute;
                content: "";
                left: 0; right: 0; top: 0; bottom: 0;
                background: rgba(200, 200, 255, 0.4);
                pointer-events: none;
              }
            `}</style>
            <EditorContent
              editor={editor}
              className="prose max-w-none h-full"
            />
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <Portal>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
            onClick={() => setShowPreviewModal(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">PDF Preview</h3>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-8 bg-gray-100/50">
                <div className="bg-white shadow-lg border border-gray-200 min-h-[800px] w-[210mm] mx-auto p-[20mm]">
                  <style>{`
                    .preview-content p { margin-bottom: 0.75em; line-height: 1.6; }
                    .preview-content h1 { font-size: 2em; font-weight: 700; margin-bottom: 0.5em; }
                    .preview-content h2 { font-size: 1.5em; font-weight: 600; margin-bottom: 0.5em; }
                    .preview-content h3 { font-size: 1.25em; font-weight: 600; margin-bottom: 0.5em; }
                    .preview-content ul, .preview-content ol { padding-left: 1.5em; margin-bottom: 1em; }
                    .preview-content ul { list-style-type: disc; }
                    .preview-content ol { list-style-type: decimal; }
                    .preview-content table { border-collapse: collapse; width: 100%; margin: 1em 0; }
                    .preview-content th, .preview-content td { border: 1px solid #e5e7eb; padding: 8px 12px; }
                    .preview-content th { background-color: #f9fafb; font-weight: 600; text-align: left; }
                    .preview-content blockquote { border-left: 4px solid #e5e7eb; padding-left: 1em; margin: 1em 0; color: #6b7280; }
                    .preview-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 2em 0; }
                  `}</style>
                  <div
                    className="preview-content"
                    dangerouslySetInnerHTML={{
                      __html: getPreviewHtml(),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );

  if (isFullscreen) {
    return (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[99998] flex items-center justify-center p-4 md:p-6">
          <div className="bg-white rounded-xl shadow-2xl w-full h-[95vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800 shrink-0 select-none">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-indigo-600 rounded-lg text-white">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold">Full-Screen Template Editor</h2>
                  <p className="text-[10px] text-slate-400 mt-0.5">Design rich content with auto-replacing variables</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition-all"
              >
                <Minimize2 className="w-4 h-4" />
                Exit Fullscreen
              </button>
            </div>

            {/* Split Pane */}
            <div className="flex-1 flex overflow-hidden">
              {renderVariablesSidebar()}
              <div className="flex-1 flex flex-col min-h-0 bg-white">
                {editorMarkup}
              </div>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  return editorMarkup;
};
